/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { fileService } from 'src/core/services/api/fileService';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addInvalidatePredictions } from 'src/core/utils/decorators/addInvalidatePredictions';
import { getListEntityService } from 'src/luis/core/services/entities/listEntityService';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { createSublistStore } from 'src/luis/core/stores/sublistStore';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { ListEntitySwagger, SubListSwagger } from 'src/luis/projects/entities/types/swagger/ListEntitySwagger';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { LuisId } from 'src/luis/types/Resource';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';

export type ListEntityHandlerGroup = ReturnType<typeof createListEntityHandlerGroup>;

/**
 * Represents a handler group for list entity operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createListEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const { addOrReplaceEntityStore, getAppVersionStore, getEntityStore } = getEntityHandlerGroupUtils(rootStore);

    const logger = getLogger();

    /**
     * Creates a new list entity.
     *
     * @param name The list entity name.
     * @param sublists The list entity sublists.
     */
    const createListEntity = async ({ name, sublists, context, tracker }: HandlerParams<Pick<ListEntity, 'name' | 'sublists'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<ListEntityStore>('CreateListEntity', (result, error) => {
                const logData = { ...context, entityName: name };

                return error ? logData : { ...logData, entityId: result.id };
            });

            const sublistStores = sublists.map(s => createSublistStore('', { name: s.name, values: s.values }));
            const listEntityStore = await logTimedActivity(listEntityService.createEntity({ name, sublists: sublistStores }));

            getAppVersionStore({ appId, versionId }).entityStoreHouse.add(listEntityStore);

            if (tracker) {
                tracker.setContext(listEntityStore);
            }
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Updates list entity.
     *
     * @param id The id of the entity to update.
     * @param name Optional: the list entity new name.
     * @param sublists Optional: the list entity new sublists.
     */
    const updateListEntity = async ({
        id,
        name,
        sublists,
        context
    }: HandlerParams<Partial<Pick<ListEntity, 'id' | 'name' | 'sublists'>>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });

            const logTimedActivity = logger.createTimedActivityLogger<ListEntitySwagger>('UpdateListEntity', () => ({
                ...context,
                entityName: name
            }));

            await logTimedActivity(listEntityService.updateEntity({ id, name, sublists }));

            const entityStore = getEntityStore<ListEntityStore>({ appId, versionId, entityId: id });
            entityStore.rename(name);
            entityStore.setSublists(sublists.map(sublist => createSublistStore(sublist.id, sublist)));
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Adds a synonym to a sublist.
     *
     * @param appId The id of the app to create the list entity in.
     * @param versionId The id of the version to create the list entity in.
     * @param id The id of the entity to update.
     * @param name The list entity new name.
     * @param value The new synonym to be added to the sublist.
     */
    const addSynonymToSublist = async ({
        id,
        context,
        value,
        name
    }: HandlerParams<Pick<ListEntityStore, 'id'> & { value: string; name: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;

            const { entityStoreHouse } = getAppVersionStore({ appId, versionId });
            const listEntityStore = <ListEntityStore>entityStoreHouse.findById(id);

            const sublist = listEntityStore.sublists.find(({ name: sublistName }) => sublistName === name);

            if (sublist) {
                if (!sublist.values.includes(value)) {
                    sublist.addValues([value]);
                }
            }

            const listEntityService = getListEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('AddSynonymToSublist', () => ({ ...context, entityId: id, sublist }));

            await logTimedActivity(listEntityService.updateSublist({ id, sublist }));
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Delete the given list entity.
     *
     * @param id The id of entity to delete.
     */
    const deleteListEntity = async ({ id, context, tracker }: HandlerParams<Pick<ListEntityStore, 'id'>>) =>
        flow(async () => {
            if (tracker) {
                tracker.setContext(id);
            }

            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('DeleteListEntity', () => ({ ...context, entityId: id }));

            await logTimedActivity(listEntityService.deleteEntity({ id }));

            const appVersionStore = getAppVersionStore({ appId, versionId });
            appVersionStore.entityStoreHouse.removeById(id);
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Adds sublist to the given list entity.
     *
     * @param id The id of list entity to add sublist to.
     * @param sublist The sublist to be added.
     */
    const addSublistToListEntity = async ({
        id,
        sublist,
        tracker,
        context
    }: HandlerParams<Pick<ListEntityStore, 'id'> & { sublist: Pick<SublistStore, 'name' | 'values'> }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<LuisId>('AddSublistToListEntity', () => ({
                ...context,
                entityId: id,
                sublist
            }));

            const sublistId = await logTimedActivity(listEntityService.addSublist({ id, sublist }));

            const entityStore = getEntityStore<ListEntityStore>({ appId, versionId, entityId: id });
            entityStore.addSublist(createSublistStore(sublistId, { ...sublist }));

            if (tracker) {
                tracker.setContext(sublist.name);
            }
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Updates a sublist in the given list entity.
     *
     * @param id The id of list entity to update the sublist for.
     * @param sublist The new sublist.
     */
    const updateSublistForListEntity = async ({
        id: entityId,
        sublist,
        context
    }: HandlerParams<Pick<ListEntity, 'id'> & { sublist: Pick<SublistStore, 'id' | 'name' | 'values'> }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('UpdateSublistForListEntity', () => ({
                ...context,
                entityId,
                sublist
            }));

            await logTimedActivity(listEntityService.updateSublist({ id: entityId, sublist }));

            const entityStore = getEntityStore<ListEntityStore>({ appId, versionId, entityId });
            const index = entityStore.sublists.findIndex(sl => sl.id === sublist.id);

            if (index !== -1) {
                entityStore.setSublist(index, createSublistStore(sublist.id, { ...entityStore.sublists[index], ...sublist }));
            }
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Deletes a sublist in the given list entity.
     *
     * @param id The id of list entity to update the sublist for.
     * @param sublists The sublists to be deleted.
     */
    const deleteSublistsForListEntity = async ({
        id,
        sublists,
        context
    }: HandlerParams<Pick<ListEntityStore, 'id'> & { sublists: Pick<SublistStore, 'id' | 'name' | 'values'>[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const listEntityService = getListEntityService({ context });
            const entityStore = getEntityStore<ListEntityStore>({ appId, versionId, entityId: id });
            const logTimedActivity = logger.createTimedActivityLogger('DeleteSublistsForListEntity', () => ({ ...context, entityId: id }));

            const sublistsWithErrors: LuisId[] = [];

            const promises = sublists.map(async sublist => {
                try {
                    await logTimedActivity(listEntityService.deleteSublist({ id, sublist }));
                    entityStore.deleteSublists([sublist.id]);
                } catch (e) {
                    sublistsWithErrors.push(sublist.id);
                }
            });

            await Promise.all(promises);
            if (sublistsWithErrors.length) {
                throw new Error();
            }
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Imports the given sublist values.
     *
     * @param param0.importedFile The imported JSON file.
     * @param param0.id The id of the updated entity.
     */
    const importSublistValues = async ({
        importedFile,
        id: entityId,
        context
    }: HandlerParams<Pick<ListEntityStore, 'id'> & { importedFile: File }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const entityStore = getEntityStore<ListEntityStore>({ appId, versionId, entityId });

            const importedSublistsString = await fileService.readFromDisk(importedFile);
            const importedSublistsJson = <Pick<SubListSwagger, 'canonicalForm' | 'list'>[]>JSON.parse(importedSublistsString);

            const listEntityService = getListEntityService({ context });
            const importedSublists = [
                ...importedSublistsJson.map(sublist => createSublistStore(-1, { name: sublist.canonicalForm, values: sublist.list }))
            ];
            const logTimedActivity = logger.createTimedActivityLogger<ListEntitySwagger>('ImportSublistValues', () => ({
                ...context,
                entityId
            }));

            const entity = await logTimedActivity(
                listEntityService.updateEntity({
                    ...entityStore,
                    sublists: [...entityStore.sublists, ...importedSublists]
                })
            );

            const listEntityStore = entitySwaggerToEntityStore(entity);

            addOrReplaceEntityStore({ appId, versionId }, listEntityStore);
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    return {
        createListEntity,
        updateListEntity,
        deleteListEntity,
        addSublistToListEntity,
        updateSublistForListEntity,
        deleteSublistsForListEntity,
        importSublistValues,
        addSynonymToSublist
    };
};
